import { IconProps } from '@thecvlb/design-system/lib/src/common/Icon';

import { SignupConfirmLinkKey } from 'modals/TermsAndRules/termsAndRules.types';

import { LINK_TO_ACCESSIBILITY } from 'constants/externalLinks';
import { ScreenSizes } from 'utils/enums';
import { PathName } from 'router/paths';

import ChatImage from 'assets/images/auth/sign-in/login-bg-2.webp';
import ProvidersRowsImage from 'assets/images/auth/sign-in/login-bg-3.webp';
import YourPrescriptionImage from 'assets/images/auth/sign-in/login-bg-4.webp';
import TreatmentsImage from 'assets/images/auth/sign-in/login-bg-5.webp';
import MobileDemoImage from 'assets/images/auth/sign-in/login-bg-6.webp';

type AccountTab = {
  icon: IconProps['name'];
  label: string;
  path: string;
};

type LegalLink = {
  icon: IconProps['name'];
  name: string;
  path: SignupConfirmLinkKey;
};

export const ASPECT_RATIO = 16 / 9;

export const LOS_ANGELES_COORDS = {
  lat: 34.0522,
  lng: -118.2437
};

export const SUPPORT_PHONE_NUMBER = {
  link: 'tel:8008521575',
  text: '(800) 852-1575'
};

export const SIGNUP_SUPPORT_PHONE_NUMBER = {
  link: 'tel:8003720469',
  text: '(800) 372-0469'
};

export const DOC_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];

export const TAILWIND_BREAKPOINTS: { [key in ScreenSizes]: number } = {
  '2xl': 1536,
  lg: 1024,
  md: 768,
  sm: 640,
  xl: 1280,
  xs: 0
};

export const MAP_STYLES = [
  {
    elementType: 'all',
    featureType: 'landscape',
    stylers: [
      {
        color: '#f3f6f6'
      }
    ]
  },
  {
    elementType: 'all',
    featureType: 'poi',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    elementType: 'geometry.fill',
    featureType: 'road',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    elementType: 'geometry.stroke',
    featureType: 'road',
    stylers: [
      {
        color: '#d3dfdf'
      },
      {
        weight: '1.10'
      }
    ]
  },
  {
    elementType: 'geometry',
    featureType: 'road.highway',
    stylers: [
      {
        visibility: 'on'
      }
    ]
  },
  {
    elementType: 'labels',
    featureType: 'road.highway',
    stylers: [
      {
        visibility: 'simplified'
      }
    ]
  },
  {
    elementType: 'geometry',
    featureType: 'water',
    stylers: [
      {
        color: '#dde5e6'
      }
    ]
  }
];

export const MY_ACCOUNT_TABS: AccountTab[] = [
  {
    icon: 'patient-filled',
    label: 'Account information',
    path: PathName.AccountInformation
  },
  { icon: 'document', label: 'Insurance', path: PathName.Insurance },
  { icon: 'bell', label: 'Notifications', path: PathName.Notifications },
  { icon: 'checkout', label: 'Billing & card', path: PathName.BillingDetails },
  { icon: 'lock', label: 'Sign in & security', path: PathName.SignInAndSecurity }
];

export const HEALTH_PROFILE_TABS: AccountTab[] = [
  {
    icon: 'reports',
    label: 'Health metrics',
    path: PathName.HealthMetrics
  },
  {
    icon: 'heartbeat',
    label: 'Health conditions',
    path: PathName.HealthConditions
  },
  {
    icon: 'lungs',
    label: 'Chronic diseases',
    path: PathName.ChronicDiseases
  },
  {
    icon: 'female',
    label: `Women's health`,
    path: PathName.WomensHealth
  },
  {
    icon: 'martini',
    label: 'Social history',
    path: PathName.SocialHistory
  },
  {
    icon: 'prescription',
    label: 'Medications',
    path: PathName.Medications
  },
  {
    icon: 'allergy',
    label: 'Allergies',
    path: PathName.Allergies
  },
  {
    icon: 'clock',
    label: 'Medical records',
    path: PathName.MedicalRecords
  }
];

export const PRESCRIPTIONS_TABS: {
  label: string;
  path: string;
}[] = [
  {
    label: 'My prescriptions',
    path: PathName.MyPrescriptions
  },
  { label: 'Pharmacy', path: PathName.Pharmacy },
  { label: 'Discount card', path: PathName.DiscountCard }
];

export const ACCOUNT_ABOUT_LINKS: { icon: IconProps['name']; name: string; path: string }[] = [
  {
    icon: 'lock',
    name: 'Legal',
    path: PathName.Legal
  },
  {
    icon: 'accessibility',
    name: 'Accessibility',
    path: LINK_TO_ACCESSIBILITY
  }
];

export const ACCOUNT_LEGAL_LINKS: LegalLink[] = [
  { icon: 'document', name: 'Privacy policy', path: 'privacy' },
  {
    icon: 'document',
    name: 'Notice of Privacy Practices',
    path: 'privacyPractices'
  },
  { icon: 'document', name: 'Terms', path: 'terms' },
  {
    icon: 'document',
    name: 'Consent to Telehealth',
    path: 'telehealth'
  }
];

export const LOGIN_PREVIEW_SLIDES = [
  {
    imageAsBackground: true,
    text: 'Take the complication out of healthcare. Getting answers and treatments has never been easier or more affordable.',
    title: 'Elevate Your Healthcare.'
  },
  {
    imageClassName: 'h-[45dvh]',
    imageUrl: ChatImage,
    text: 'Now you can skip the waiting rooms and get the expert medical guidance you need, 24/7.',
    title: 'Around The Clock Care'
  },
  {
    className: 'pt-4',
    imageClassName: 'top-0',
    imageUrl: ProvidersRowsImage,
    text: 'No matter where you are, feel confident that your medical team is ready to help you feel your best.',
    title: 'Meet Your Medical Team'
  },
  {
    imageClassName: 'max-w-[280px] h-[45dvh]',
    imageUrl: YourPrescriptionImage,
    text: 'Hassle-free prescriptions. Plus, use your free prescription discount card to save up to 92% on your medication!',
    title: 'Prescriptions Made Easy'
  },
  {
    imageClassName: 'max-w-[280px] h-[45dvh]',
    imageUrl: TreatmentsImage,
    text: 'Address your primary, urgent, and chronic health needs from the comfort of your own home.',
    title: 'Skip The Drive'
  },
  {
    imageClassName: 'h-[45dvh]',
    imageUrl: MobileDemoImage,
    text: 'Just one app to view your health information, schedule appointments, and manage your prescriptions and more.',
    title: 'Your Health At Your Fingertips'
  }
];
